/**
 * locomotion.ts — Pure helpers for turning a velocity into animation state.
 *
 * Phaser-free (and Three-free) so it stays unit-testable. Used by Player and Dog
 * to decide "am I walking?" and "which way am I facing?".
 */

/** Whether the entity is moving fast enough to play its walk animation (units/s). */
export function isMoving(vx: number, vy: number, threshold = 6): boolean {
  return Math.hypot(vx, vy) > threshold;
}

/**
 * Yaw angle (radians) for a body travelling at (vx, vy) on the ground plane.
 *
 * In 3D we rotate the model instead of flipping a sprite. Gameplay y maps to world
 * z, and Three.js yaw is measured about the +y axis, so a body facing +x has yaw 0
 * and increasing yaw turns toward -z. `Math.atan2(vx, vy)` gives exactly that.
 *
 * Below `deadzone` speed the current yaw is kept, so a stationary body doesn't
 * snap to an arbitrary direction.
 */
export function facingYaw(vx: number, vy: number, currentYaw: number, deadzone = 2): number {
  if (Math.hypot(vx, vy) < deadzone) return currentYaw;
  return Math.atan2(vx, vy);
}

/**
 * Shortest angular step from `from` toward `to`, scaled by `t` (0..1).
 *
 * Turning naively toward a target yaw can spin the long way round when the angle
 * wraps past ±π; this always takes the short arc.
 */
export function turnToward(from: number, to: number, t: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return from + delta * t;
}
